import {
  RAISE_CEILING, RAISE_MIN_LEAD, RAISE_MIN_ROOMS, LOWER_MAX_LEAD, HORIZON_DAYS,
} from './rules';

// 卡片標題不寫範圍 (各列範圍不同)，每一列自己交代
const RAISE_RANGE = `未來 ${RAISE_MIN_LEAD}-${HORIZON_DAYS} 天內`;

/**
 * 這張卡上所有給使用者看的文字 —— 三列摘要與兩個明細 Drawer 的標題與說明。
 *
 * 集中在這裡而不是散在 JSX：那三行是整張卡唯一會被讀到的東西 (表格要點開才有)，
 * 數字或門檻寫錯不會讓任何測試變紅，只會讓人照著錯的資訊做決定，所以值得被單獨測。
 *
 * 門檻一律從 rules.js 的常數帶入，不寫死在字串裡 ——
 * 否則改一個常數要記得同步改好幾處文案，而漏改不會有任何訊號。
 *
 * 摘要文字帶影響金額而不帶日期：金額決定「先點哪一列」，日期點開就看得到，
 * 三列都擠滿日期只會變吵。
 */

const money = (v) => (v == null ? '—' : `NT$${Math.round(v).toLocaleString()}`);
const n = (v) => Math.round(v || 0).toLocaleString();
const sumBy = (list, key) => (list || []).reduce((s, x) => s + (x[key] || 0), 0);

/** 兩張清單的空狀態文案。講「為什麼沒有」而不是只寫「無資料」，並帶出該類的門檻。 */
export const EMPTY_TEXT = {
  raise: `${RAISE_RANGE}沒有悲觀預測超過 ${RAISE_CEILING}% 的日子 (另需待售 ${RAISE_MIN_ROOMS} 間以上)`,
  lower: `行動窗口 (未來 ${LOWER_MAX_LEAD} 天) 內沒有明確落後的日子`,
};

/**
 * 調漲上界的理由。卡片標頭的上界提示與調漲 Drawer 共用這一句 ——
 * 上界不開放調整，使用者唯一能理解「為什麼不是跟著目標走」的地方就是這段話。
 */
export const RAISE_CEILING_REASON = `調漲本身會讓住房率往下掉，判斷時要把這個影響算進去：`
  + `只在悲觀預測 (P25) 超過 ${RAISE_CEILING}% 時才建議調漲，超出 ${RAISE_CEILING}% 的部分就是漲價後可以讓掉、仍能維持在 ${RAISE_CEILING}% 以上的緩衝。`
  + '這個上界與目標住房率無關：目標是業績要賣到哪裡，調漲要回答的是需求是否多到房間裝不下。';

const PREDICTION = '預測最終 = 目前在手 + 近 5 年同提前天數的剩餘進帳率 (P25–P75)。';

/**
 * 表頭問號圖示的說明。兩張表的「潛在房租」與 10% 損益兩平欄同名或對稱，
 * 但算法與估算基礎不同，不寫出來會被當成同一個口徑。抽屜說明引用同一份文字。
 */
export const COLUMN_HELP = {
  raiseOverflow: '潛在房租：依中位預測，滿房後預計擋掉的房租 = (中位預測 - 100%) × 容量 × 目前 ADR；'
    + '中位預測沒超過 100% 時為 0。',
  lowerUpside: '潛在房租：待售房若全部賣出可得的房租 = 待售間夜 × 目前 ADR。',
  raiseFloor10: '漲10%可掉到：以悲觀預測 (P25) 為基礎，超過 100% 時以 100% 計 (超出的部分本來就賣不出去)。'
    + '算法 = min(悲觀預測, 100%) ÷ 1.1；漲價 10% 後最終住房率掉到這裡為止，RevPAR 都不會變少。',
  lowerBreakEven10: '降10%需填到：以目前在手率為基礎。算法 = 在手率 ÷ 0.9；'
    + '降價 10% 後在手要填到這裡，RevPAR 才不會變少。',
};

/**
 * 每一類明細 Drawer 的標題與說明。
 *
 * 兩類各自一個 Drawer 而不是用分頁擠在一起：點哪一項就只看那一項，不必進去還要再找分頁。
 *
 * 每段都要交代**過濾門檻**：清單只列 7 天不代表未來 90 天只有 7 天偏弱，
 * 而是行動窗口把其餘的濾掉了。不講清楚會被讀成前者。
 */
export const DRAWER_META = {
  raise: {
    title: '建議調漲',
    note: `${PREDICTION}${RAISE_CEILING_REASON}`
      + `另需提前 ${RAISE_MIN_LEAD} 天以上 (太近才漲來不及發酵) 且待售 ${RAISE_MIN_ROOMS} 間以上 `
      + '(只剩幾間時漲價影響的房量太少)，故本清單不等於「所有預測會滿房的日子」。'
      + '「超出上界」欄就是上述緩衝的百分點。'
      + `${COLUMN_HELP.raiseOverflow}和調降表的潛在房租不同，那裡是待售房若全部賣出可得的房租。`
      + `「團體撐盤」：扣掉在手團體後，悲觀預測就不到 ${RAISE_CEILING}%，`
      + '團體一取消就會掉下來，漲價前先確認團體訂金與名單。'
      + '前一晚、後一晚是鄰日的中位預測，不是在手率：遠期的在手率每天看起來都很弱。'
      + '鄰日中位預測不到目標住房率時提示最短住宿 (特殊假日 3 晚、其餘 2 晚)，要不要設由你判斷。'
      + `${COLUMN_HELP.raiseFloor10}不建議調價幅度：那個數字沒有資料支撐。`,
  },
  lower: {
    title: '建議調降',
    note: `${PREDICTION}只在預測的整個區間都低於目標時才列入，跨過目標的日子不給建議。`
      + `另只列提前 ${LOWER_MAX_LEAD} 天以內的日子：散客訂房多集中在入住前一個月內，`
      + '更早的低在手多屬正常提前節奏，此時降價常是白降。'
      + '所以本清單不等於「所有落後的日子」：下方展望的「落後正常帶」不限提前天數，'
      + `會包含 ${LOWER_MAX_LEAD} 天以後的日子，那些日子會隨時間進入這個窗口。`
      + '「降10%需填到」是損益兩平的算術：RevPAR = ADR × 在手，降 10% 就得把在手填到該數字才不傷 RevPAR，'
      + '填不到就是降價傷 RevPAR。',
  },
};

/** 沒有日子的那一列：轉灰、不可點，文案與 Drawer 的空狀態共用同一份。 */
const emptyRow = (key) => ({ key, tone: 'none', tag: '無', text: EMPTY_TEXT[key], clickable: false });

function targetRow(sum, target, horizonDays) {
  if (!sum) {
    return { key: 'target', tone: 'none', tag: '無', clickable: false,
      text: '尚無足夠資料計算業績目標' };
  }
  // 缺口 <= 0 就沒有「還要賣多少、賣什麼價」可言，講超標與守價才有意義
  if (sum.gap <= 0) {
    return { key: 'target', tone: 'pos', tag: '達標', clickable: false,
      text: `未來 ${horizonDays} 天已達 ${target}% 住房目標，超出 ${money(Math.abs(sum.gap))}，`
        + '守價優先，收回不必要的折扣' };
  }
  // 寫成完整句子而不是「距目標還差 X —— 需再賣 Y」那種條列：
  // 這一行是給業務看的任務書，「還差 3,691 間夜」聽起來像天文數字，
  // 但真正要主動去做的只有 2,079 間 —— 把「自己會進來的」與「要去搶的」分開講，
  // 那個落差才是這一行存在的意義。
  //
  // 句尾的可售量是可行性揭露：需賣間夜若逼近或超過尚可售間夜，這個目標量能上就追不回來、
  // 只能靠價格。這一列刻意不可點、沒有明細可退，那個判斷只能在這裡給。
  const feasibility = sum.coverage == null ? ''
    : `窗口內尚有 ${n(sum.sellableNights - sum.otbNights)} 間夜可售，需去化其中 ${sum.coverage}%。`;
  return { key: 'target', tone: 'warn', tag: '缺口', clickable: false,
    text: `未來 ${horizonDays} 天要達到 ${target}% 住房，總共還要賣掉 ${n(sum.needNights)} 間夜`
      + ` (距目標 ${money(sum.gap)}、平均單價需達 ${money(sum.needAdr)})。`
      + `其中 ${n(sum.naturalNights)} 間根據歷史節奏會自己進來，`
      + `需要額外主動銷售 ${n(sum.fightNights)} 間。${feasibility}` };
}

export function summaryRows({ sum, raise, lower, target, horizonDays }) {
  const rows = [targetRow(sum, target, horizonDays)];

  // 團體撐盤與鄰日偏弱要在摘要就點出來：前者是漲價前必須先排除的風險，
  // 後者是漲價之外的另一個手段，兩者都要點開明細才看得到的話很容易被略過
  const raiseDays = raise || [];
  const riskN = raiseDays.filter((d) => d.groupRisk).length;
  const weakN = raiseDays.filter((d) => d.weakSides && d.weakSides.length).length;
  const raiseNotes = [];
  if (riskN) raiseNotes.push(`${riskN} 天靠團體撐盤，漲價前先確認團體`);
  if (weakN) raiseNotes.push(`${weakN} 天鄰日偏弱，可考慮最短住宿`);
  const overflowSum = sumBy(raiseDays, 'overflow');
  rows.push(raiseDays.length
    ? { key: 'raise', tone: 'pos', tag: '機會', clickable: true,
        text: (overflowSum > 0
          ? `${RAISE_RANGE}建議調漲 ${raiseDays.length} 天，滿房後預計擋掉 ${money(overflowSum)} 房租`
          : `${RAISE_RANGE}建議調漲 ${raiseDays.length} 天：悲觀預測都超過 ${RAISE_CEILING}%`)
          + (raiseNotes.length ? `。其中 ${raiseNotes.join('；')}。` : '') }
    : emptyRow('raise'));

  const lowerDays = lower || [];
  rows.push(lowerDays.length
    ? { key: 'lower', tone: 'warn', tag: '需處理', clickable: true,
        text: `未來 ${LOWER_MAX_LEAD} 天內建議調降 ${lowerDays.length} 天，合計 ${n(sumBy(lowerDays, 'unsold'))} 間夜待售 ≈ 潛在房租 `
          + `${money(sumBy(lowerDays, 'upside'))}，樂觀預測仍達不到目標` }
    : emptyRow('lower'));

  return rows;
}
